import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = '__DEV_FEATURE_GATE_OVERRIDES__';

type OverrideValue = boolean | null; // null = no override (use Statsig value)
type Overrides = Record<string, OverrideValue>;

interface FeatureGateOverridesContextValue {
    overrides: Overrides;
    setOverride: (gateName: string, value: OverrideValue) => void;
    clearAll: () => void;
}

const FeatureGateOverridesContext = createContext<FeatureGateOverridesContextValue>({
    overrides: {},
    setOverride: () => {},
    clearAll: () => {},
});

function loadOverrides(): Overrides {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
}

function saveOverrides(overrides: Overrides) {
    const cleaned = Object.fromEntries(Object.entries(overrides).filter(([, v]) => v !== null));
    if (Object.keys(cleaned).length === 0) {
        localStorage.removeItem(STORAGE_KEY);
    } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
    }
}

export default function FeatureGateOverridesProvider({ children }: { children: React.ReactNode }) {
    const [overrides, setOverrides] = useState<Overrides>(loadOverrides);

    const setOverride = useCallback((gateName: string, value: OverrideValue) => {
        setOverrides((prev) => {
            const next = { ...prev, [gateName]: value };
            saveOverrides(next);
            return next;
        });
    }, []);

    const clearAll = useCallback(() => {
        setOverrides({});
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    const value = useMemo(() => ({ overrides, setOverride, clearAll }), [overrides, setOverride, clearAll]);

    return <FeatureGateOverridesContext.Provider value={value}>{children}</FeatureGateOverridesContext.Provider>;
}

export function useFeatureGateOverrides() {
    return useContext(FeatureGateOverridesContext);
}
